/** Grid no-rows overlay — an empty screen is an invitation to act, not a dead
 *  end. Three cases:
 *  - filters/search active → say so and offer one-click "Clear filters";
 *  - genuinely empty, but the CONSOLE SCOPE is doing the filtering → name the
 *    tenants ("Acme Adventures · Both +1", like the AppBar chip), so a scoped
 *    empty grid is never read as "no records exist";
 *  - genuinely empty → the page's own invitation copy + optional Create action.
 */
import BusinessIcon from '@mui/icons-material/Business';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Button, Chip, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { summarizeSelection } from '@/components/TenantPicker/clients';
import { useTenantSelection } from '@/components/TenantPicker/TenantPicker';
import { setTenantScopeList, useTenantScopeList } from '@/scope/tenantScope';

export function GridEmptyState({
  filtersActive,
  onClearFilters,
  message,
  action,
}: {
  filtersActive: boolean;
  onClearFilters?: (() => void) | undefined;
  message: string;
  action?: ReactNode;
}) {
  // Briefs the AppBar chip already looked up; no request of its own.
  const scope = summarizeSelection(useTenantSelection(useTenantScopeList(), { lookup: false }));
  return (
    <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center', p: 4 }} spacing={1.5}>
      <Typography color="text.secondary">
        {filtersActive ? 'No results match your filters.' : message}
      </Typography>
      {!filtersActive && scope && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            The console is scoped to
          </Typography>
          <Chip
            size="small"
            icon={<BusinessIcon />}
            label={scope.text}
            onDelete={() => setTenantScopeList([])}
            aria-label={`Clear console scope: ${scope.full}`}
          />
        </Stack>
      )}
      {filtersActive
        ? onClearFilters && (
            <Button size="small" startIcon={<FilterAltOffIcon />} onClick={onClearFilters}>
              Clear filters
            </Button>
          )
        : action}
    </Stack>
  );
}
